import { Document, ObjectId, Schema } from "mongoose";
import {
  DayOfWeek,
  Hour,
  Minute,
  NestedKeys,
  DateRange,
  Sorted,
  DeepReplace,
} from "./HelperTypes";
import {
  ApiAsset,
  ApiAssetType,
  ApiEndUserAccount,
  ApiProductOfferings,
} from "./RedBeeTypes";
import { IAsset as RBMAsset } from "../shared/types/RedBee.types";
import { CountryCode, TimezoneName } from "countries-and-timezones";
import { UpsalesProductId } from "../shared/types/Upsales.types";
import { SentMessageInfo } from "nodemailer";
import { CmsAsset, SourceType } from "./cms/CmsAssetType";
import { RedbeeFormattedAsset } from "@server/utils/transformers/assetTransformer";
import { IPurchase } from "@server/models/purchaseModel";
import { AdProvider } from "@server/services/ads/types";

export interface IPricing {
  price: number;
  currency: string;
  showPrice: boolean;
}

export type Status =
  | LoadingStatus.IDLE
  | LoadingStatus.LOADING
  | LoadingStatus.SUCCEEDED
  | LoadingStatus.FAILED;

export enum LoadingStatus {
  IDLE = "idle",
  LOADING = "loading",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
}

export interface IRoomLabel {
  label: string;
  value: string;
}

export enum DeviceOS {
  WEB_OS = "webOS",
  TIZEN = "Tizen",
  ANDROID_TV = "AndroidTV",
}

export enum NavigationLevel {
  COMPANY = "company",
  PREMISE = "premise",
  ADMIN = "admin",
}

export enum NendaProduct {
  STREAMING_APP = "STREAMING_APP",
  FRONTLAYER = "FRONTLAYER",
  SIGNAGE = "SIGNAGE",
  SIGNAL_DELIVERY = "SIGNAL_DELIVERY",
}

// -- FRONTLAYER --

export interface IDeviceApplication {
  platformAppIds: Partial<Record<DeviceOS, string>>;
}

export enum FrontLayerItemType {
  APPLICATION = "application",
  ROUTE = "route",
  IMAGE = "image",
}

export enum FrontLayerInformationSource {
  URL = "url",
  IMAGE = "image",
  HTML = "html",
}
interface AbstractFrontLayerInformationItem {
  name: string;
}

export interface FrontLayerInformationURLItem
  extends AbstractFrontLayerInformationItem {
  sourceType: FrontLayerInformationSource.URL;
  content: IFrontLayerInformationURLContent;
}

export interface FrontLayerInformationImageItem
  extends AbstractFrontLayerInformationItem {
  sourceType: FrontLayerInformationSource.IMAGE;
  content: IFrontLayerInformationImageContent;
}

export interface FrontLayerInformationHTMLItem
  extends AbstractFrontLayerInformationItem {
  sourceType: FrontLayerInformationSource.HTML;
  content: IFrontLayerInformationHTMLContent;
}

export type FrontLayerInformationItem =
  | FrontLayerInformationURLItem
  | FrontLayerInformationImageItem
  | FrontLayerInformationHTMLItem;

export interface IFrontLayerInformationURLContent {
  locator: string;
}

export interface IFrontLayerInformationImageContent {
  images: IFrontLayerInformationImage[];
}

export type IFrontLayerInformationImage = Sorted<string, "locator">;

export interface IFrontLayerInformationHTMLContent {
  html: string;
}

export enum FrontLayerBackgroundType {
  COLOR = "color",
  IMAGE = "image",
}

export interface IRadioPage extends IFrontLayerSubpage<IRadioStation> {
  selectedLocalP4Station: string;
}

export type IApplicationsPage = IFrontLayerSubpage<IFrontLayerApplication>;

export type IFrontLayerInformationPage =
  IFrontLayerSubpage<FrontLayerInformationItem>;

export interface IFrontLayerApplication extends IFrontLayerItem {
  application: IDeviceApplication;
}

type Localisation = Record<string, string>;

export interface IFrontLayerItem {
  id: string;
  name?: Localisation;
  iconName?: string;
  backgroundColor?: string;
  selectedBackgroundColor?: string;
  font?: string;
  fontColor?: string;
  selectedFontColor?: string;
  borderColor?: string;
  selectedBorderColor?: string;
  imageUrl?: string;
  backgroundImageUrl?: string;
  backgroundTypes: FrontLayerBackgroundType[];
  iconType: "image" | "icon";
  enabled: boolean;
  type: FrontLayerItemType;
  description?: Localisation;
}

export interface IFrontLayerClockConfiguration {
  font: string;
  color: string;
}

export interface IFrontLayerSubpage<
  T extends IFrontLayerItem | IRadioStation | FrontLayerInformationItem,
> extends IFrontLayerItemDesignConfiguration {
  items: Array<T>;
  backgroundTypes: FrontLayerBackgroundType[];
  backgroundColor?: string;
  backgroundImageUrl?: string;
}

export enum FrontLayerSectionType {
  HEADER = "header",
  FOOTER = "footer",
}

export interface IFrontLayerVerticalSection {
  backgroundColor?: string;
}

export interface IFrontLayerItemDesignConfiguration {
  fontColor?: string;
  selectedFontColor?: string;
  backgroundImageUrl?: string;
  defaultItemColor?: string;
  defaultSelectedItemColor?: string;
  defaultItemBorderColor?: string;
  defaultSelectedItemBorderColor?: string;
  isBorderSelectionEnabled?: boolean;
}

export interface IFrontlayerConfiguration
  extends IBaseModel,
    IFrontlayerConfigValues {
  name: string;
  company: Document["_id"];
  premise?: Document["_id"];
}

export interface IFrontlayerConfigValues {
  logoUrl?: string;
  backgroundTypes: FrontLayerBackgroundType[];
  backgroundColor?: string;
  backgroundImageUrl?: string;
  font: string;
  fontColor: string;
  imageItemUrl?: string;
  defaultItemColor: string;
  defaultSelectedItemColor: string;
  defaultItemBorderColor: string;
  defaultSelectedItemBorderColor: string;
  defaultSelectedFontColor: string;
  isBorderSelectionEnabled: boolean;
  information: IFrontLayerInformationPage;
  radio: IRadioPage;
  applications: IApplicationsPage;
  clock: IFrontLayerClockConfiguration;
  homeScreenItems: Array<IFrontLayerItem | IFrontLayerApplication>;
  backButton: IFrontLayerItemDesignConfiguration;
  header: IFrontLayerVerticalSection;
  footer: IFrontLayerVerticalSection;
}

export const SignageSlotAmountValues = [0, 10, 20] as const;
export const ChannelOverrideDurationValues = [
  5, 15, 30, 45, 60, 90, 120, 240, 480,
] as const;
export type SignageSlotAmount = (typeof SignageSlotAmountValues)[number];

export enum ScheduleContentRefType {
  CONTENT_CHANNEL = "ContentChannel",
  SIGNAGE_PLAYLIST = "SignagePlaylist",
}
export const scheduledContentRefTypes = [
  ScheduleContentRefType.CONTENT_CHANNEL,
  ScheduleContentRefType.SIGNAGE_PLAYLIST,
] as const;

export type ScheduledContentRefType = (typeof scheduledContentRefTypes)[number];

export interface IContentOverride {
  content: Document["_id"];
  contentRefType: ScheduledContentRefType;
  from: Date;
  to: Date;
}

export interface IWeeklyScheduleItem {
  screenId: string;
  schedule: { [key: string]: string | null };
}

export enum ScreenOrientation {
  LANDSCAPE = "landscape",
  PORTRAIT = "portrait",
}

export const screenOrientations = [
  ScreenOrientation.LANDSCAPE,
  ScreenOrientation.PORTRAIT,
];

export interface ISignageConfig {
  numberOfSceens?: number;
  screenOrientation: ScreenOrientation;
  isSignageAsContentEnabled: boolean;
  arePromotionsEnabled: boolean;
  /**
   * Whether web-page (`AssetType.URL`) assets may be created and played at this
   * premise. Off by default: not every screen's hardware/autoplayer build can
   * render a web page, and we have no per-screen capability data to decide it
   * automatically.
   */
  areUrlAssetsEnabled: boolean;
  adPermissions: IAdPermission;
  numberOfHourlySignageSlots?: SignageSlotAmount;
  isSoundEnabled?: boolean;
  contentOverride?: IContentOverride;
  // default content values are currently not nested to avoid migration
  defaultContent?: Document["_id"];
  defaultContentRefType: ScheduledContentRefType;
}

export interface IRadioStation {
  id: string;
  externalId?: string;
  name: string;
  url: string;
  disabled?: boolean;
}

export interface IFrontLayerOptions {
  availableDeviceApps: IFrontLayerApplication[];
  availableHomeScreenItems: (IFrontLayerItem | IFrontLayerApplication)[];
}

// -- ORGANIZATION UNIT --
export interface IRegex {
  areaRegex: [string, string];
  roomRegex: [string, string];
}

export enum OrganizationUnitType {
  GLOBAL = "global",
  HOTEL_CHAIN = "hotelChain",
  HOTEL = "hotel",
  ROOM = "room",
}

export interface IChannelMap {
  enabled: boolean;
  channels: Record<string, string>;
}

export interface IAddress {
  streetAddress: string;
  postalCode: string;
  city: string;
  country: CountryCode;
  timezone: TimezoneName;
  region: string;
}

export interface IContactPerson {
  fullName: string;
  phoneNumber: string;
  emailAddress: string;
}

export interface IContract {
  startDate: Date;
  endDate?: Date;
}

export interface IBusinessHours {
  hours: [string, string];
  isOpen: boolean;
}

export type WeeklyBusinessHours = Partial<Record<DayOfWeek, IBusinessHours>>;

export interface IOrganizationUnit extends IBaseModel {
  numberOfRooms: number;
  name: string;
  address: IAddress;
  contactPerson: IContactPerson;
  contract?: IContract;
  company?: string | Company;
  category?: string;
  numberOfEntries?: number; // entries per week
  dwellTimeInMinutes?: number;
  type: OrganizationUnitType;
  parent: string;
  roomTypes: Array<IRoomType>;
  redBeeName?: string;
  defaultRoomType?: Document["_id"];
  logoUrl?: string;
  hash?: string;
  upsalesCompanyId?: number;
  upsalesSubscriptionId?: number;
  streamingContentManagerEnabled: boolean;
  pmsHotelId?: string;
  pmsToken?: string;
  apiKey?: string;
  regex: IRegex;
  reseller?: string;
  systemIntegrator?: string;
  allowContinuousStreaming?: boolean;
  showNonPurchasedContent?: boolean;
  frontlayerConfig?: string | null;
  frontlayerOptions?: IFrontLayerOptions; // The available options when making a frontlayer configuration for an OU
  channelMap: IChannelMap;
  nendaProducts: Array<NendaProduct>;
  contentChannels?: string[];
  signageConfig?: Pick<
    ISignageConfig,
    | "numberOfHourlySignageSlots"
    | "isSignageAsContentEnabled"
    | "arePromotionsEnabled"
    | "areUrlAssetsEnabled"
  >;
  assets?: Partial<Record<NendaProduct, IAsset[]>>;
  businessHours?: WeeklyBusinessHours;
  adProvider?: AdProvider; // Override ad provider for this premise
  contentManagement?: ContentProductMappingData;

  findRoomType: (roomTypeId: string) => IRoomType | null;
}

export interface ProductMapping {
  name: string;
  isBaseProduct: boolean;
  roomTypeCategory: RoomTypeCategory;
  upsalesProduct: number;
  RBMOfferings: string[];
}

export type PricedProductMapping = ProductMapping & { price: number };

export interface ContentPackage extends ProductMapping {
  id: string;
  price: number;
  assets: RBMAsset[]; // TODO: this can be slimmed down
}

export interface ContentManagmentData {
  premiseId: string;
  availablePackages: Partial<Record<RoomTypeCategory, ContentPackage[]>>;
  roomContent: RoomContent[];
}

export interface RoomContent {
  roomId: string;
  roomTypeId: string;
  roomTypeCategory?: RoomTypeCategory;
  currentContent: string[];
  state: RoomContentState;
}

export enum RoomContentState {
  VALID = "valid",
  INVALID = "invalid",
}

export interface ContentProductMappingData {
  platformFeeProductIds: number[];
  productMapping: ProductMapping[];
}

// -- ROOM --
export enum PMSStateStatus {
  FREE = "Free",
  OCCUPIED = "Occupied",
}
export interface IPMSRoomState {
  messageId: string;
  roomNumber: string;
  premiseId: string;
  status: PMSStateStatus;
  guestStatus?: Array<GuestStateMapping>;
  stayDetails?: {
    arrivalDate: Date | string;
    departureDate: Date | string;
    guestFirstName?: string;
    guestLastName?: string;
    bookingId: string;
    guestSalutation?: string; // Mr., Ms, etc.
    guestLanguage?: string; // IETF language tag, e.g. "en", "en-US", "en-GB", "pl", "pl-PL"
  };
}

export interface GuestStateMapping {
  id: string;
  state: GuestState;
}

export enum GuestState {
  CHECKED_IN = 1,
  CHECKED_OUT = 2,
}

export enum RoomStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

export interface AutoplayAsset {
  enabled: boolean;
  id: string;
  type?: ApiAssetType;
}

export interface IAutocompleteAsset {
  text: any;
  type: string;
  assetId: string;
}

export interface IRoom extends IBaseModel {
  name: string;
  description: string;
  carousels: Array<Document["_id"]>;
  pages: Array<Document["_id"]>;
  redBeeAccountId: string;
  roomNumber: number;
  roomIdentifier: Schema.Types.Number | number;
  hotel: Document["_id"];
  hotelChain: Document["_id"];
  roomType: Document["_id"];
  lastPMSStatus?: IPMSRoomState;
  account?: ApiEndUserAccount;
  labels?: Array<IRoomLabel>;
  status: RoomStatus;
  deviceId?: string;
  autoplayChannelId?: number;
  autoplayAsset?: AutoplayAsset;
  nendaProducts: Array<NendaProduct>;
  signage: {
    config?: ISignageConfig;
    commands?: ScheduledCommand[];
    schedule: {
      promotions: Array<Document["_id"]>;
      content: {
        weekly: WeeklyScheduledContent;
      };
    };
  };
  group?: Document["_id"];
  groups: Array<Document["_id"]>;
  numberOfDevices: number;
  devices: string[];
  vistarVenueId?: string;
  adProvider?: AdProvider; // Override ad provider for this specific screen
  entitlement: {
    featureSets: Array<Document["_id"]>;
  };
  vilmerToken?: string;
  addLogToHistory: (status: HistoryTypes) => Promise<void>;
}

export type IRoomWithEntitlement = DeepReplace<
  IRoom,
  "entitlement",
  "featureSets",
  IFeatureSet[]
>;

export type WeeklyScheduledContent = {
  [key in DayOfWeek]?: string | ObjectId;
};

export enum DeviceType {
  TV = "tv",
  STB = "stb",
  TABLET = "tablet",
  PHONE = "phone",
  COMPUTER = "computer",
}

export interface IDevice extends IBaseModel {
  type?: DeviceType;
  manufacturer?: string;
  deviceModel?: string;
  enabled: boolean;
  operatingSystem?: {
    name: string;
    version: string;
  };
  additionalData?: any; // note sure if worth to have a field where additional data can just be dumped
  firstSeen?: Date;
  lastSeen?: Date;
  lastIP?: string;
}

// -- ROOM TYPE --
export enum RoomTypeCategory {
  ROOM = "room",
  COMMON_AREA = "common_area",
  PUBLIC = "public",
}

export interface IRoomType extends IBaseModel {
  numberOfRooms: number;
  hotelChain: Document["_id"];
  name: string;
  carousels: Array<Document["_id"]>;
  pages: Array<Document["_id"]>;
  category: RoomTypeCategory;
  numberOfReferences?: number; // Used on response
  featureSets: Array<Document["_id"]>;
}

// -- CAROUSEL --
export enum CarouselTypes {
  RECOMMEDED = "recommended",
  CURATED = "curated",
  QUERY = "query",
  CONTINUE_WATCHING = "continue_watching",
  EPG = "epg",
}

export enum LogicConnectors {
  AND = "AND",
  OR = "OR",
}

export type CarouselView = "CAROUSEL" | "SEE_ALL";

export type CarouselAssetType = Exclude<
  ApiAssetType,
  "AD" | "COLLECTION" | "PODCAST" | "PODCAST_EPISODE" | "OTHER"
>;

export interface ICarousel extends IBaseModel {
  type: CarouselTypes; // default "curated"
  assetIds: Array<string>;
  tagIds: Array<string>;
  name: string;
  title?: Array<ITitle>;
  channelId?: string;
  assetTypeFilter?: CarouselAssetType[] | "*";
  sourceType: SourceType | "";
  liveEventChannelFilter?: Array<string>;
  sort?: string;
  query?: string;
  logicConnector?: LogicConnectors;
  groupByTitle?: boolean;
  filterDuplicates?: boolean;
  showNonPurchasedContent?: boolean;
  displayLimit?: number;
}

export type CarouselAvailabilityStatus =
  | "AVAILABLE"
  | "UNAVAILABLE"
  | "PARTIAL"
  | "UNKNOWN";

export interface ICarouselWithAsset extends ICarousel {
  assets: Array<ApiAsset | CmsAsset | RedbeeFormattedAsset>;
  availabilityStatus?: CarouselAvailabilityStatus;
  totalCount: number;
}

export interface CleanedCarousel {
  id: string;
  type: CarouselTypes;
  name: string;
  title: { text: any; language: any }[];
  assets: Array<ApiAsset | CmsAsset | RedbeeFormattedAsset>;
  channelId?: string;
  daysForward?: number;
  daysBackward?: number;
  displayLimit?: number;
  query?: string;
  sort?: string;
  totalCount?: number;
  availabilityStatus?: CarouselAvailabilityStatus;
}

// -- SUBPAGE --
export interface ISubpage extends IBaseModel {
  name: string;
  carousels: Array<Document["_id"]>;
  title: Array<ITitle>;
  draft: boolean;
  icon?: string;
}

// -- USER --
export interface IUser extends IBaseModel {
  email: string;
  type: Role;
  password?: string;
  company?: Document["_id"];
  premises?: Array<Document["_id"]>;
}

export interface UserSession {
  expires: number;
  username: string;
  role: Role;
  company: string;
  premises?: string[];
  issuedAt: number;
}

export enum Role {
  // Internal Nenda roles
  Admin = "admin",
  Superuser = "basic",
  // External customer roles
  SystemIntegrator = "systemIntegrator",
  PremiseAdmin = "premiseAdmin",
  PremiseUser = "premiseUser",
  PremiseGroupUser = "premiseGroupUser",
  Reseller = "reseller",
  ResellerAdmin = "resellerAdmin",
  CompanyAdmin = "companyAdmin",
  CompanyUser = "companyUser",
}

export type SimpleUser = Pick<
  IUser,
  | "email"
  | "type"
  | "company"
  | "_id"
  | "premises"
  | "createdAt"
  | "updatedAt"
  | "password"
>;

// -- SETTINGS --
export interface ISettings {
  languages?: Array<ISettingsLanguage>;
  defaultContentBrandingLogo?: ImageOverlay;
  roomCategories?: Array<{ name: string; value: string }>;
  roomTypeCategories?: { [key: string]: RoomTypeCategory };
  showTransformationGui?: boolean;
}

export interface ISettingsLanguage {
  name: string;
  value: string;
}

// -- MISC --
export interface ITitle {
  language: string;
  text: string;
}

export enum SortDirection {
  DESCENDING = "desc",
  ASCENDING = "asc",
}

export interface IBaseModel {
  createdAt?: Date | string;
  updatedAt?: Date | string;
  _id?: Document["_id"];
  __v?: any;
}

// -- PRODUCT OFFERINGS --
export enum ProductOfferingType {
  PROVISION = "provision",
  TVOD = "tvod",
  SVOD = "svod",
}

export interface IProductOfferings extends ApiProductOfferings {
  numberOfAssets?: number;
  type?: string;
}

// -- HISTORY --
export enum HistoryTypes {
  ACTIVATION = "activation",
  DEACTIVATION = "deactivation",
  UPDATE = "update",
  CREATION = "creation",
}

export interface ITransaction {
  timestamp: string;
  type: string | HistoryTypes;
  ipAddress: string;
}

export enum ParentType {
  HOTEL_CHAIN = "hotelChain",
  HOTEL = "hotel",
  ROOM = "room",
}

export interface IHistory extends Document, IBaseModel {
  parentType: ParentType;
  transactions: Array<ITransaction>;
  parent: string;
  addTransaction: (
    type: HistoryTypes,
    ipAddress: string,
    date?: Date
  ) => Promise<void>;
  getActiveTimeBetweenDates: (from: Date, to?: Date) => Promise<number>;
}

// -- REPORTS --
export interface IReport {
  rooms?: Array<IRoomsReport>;
  subscriptions?: Array<ISubscriptionsReport>;
  packages?: Array<IPackagesReport>;
  content?: Array<IContentReport>;
  transactions?: Array<ITransactionReport>;
}
export interface IRoomsReport {
  hotelChainId: string;
  hotelId: string;
  chain: string;
  premise: string;
  roomCategory: string;
  activeRoomCount: number;
  roomCount: number;
}
export interface ISubscriptionsReport {
  hotelChainId: string;
  hotelId: string;
  chain: string;
  premise: string;
  activeRoomCount: number;
  packageName: string;
}

export interface IPackagesReport {
  hotelChainId: string;
  chain: string;
  packageName: string;
  assetNames: string;
}

export interface IContentReport {
  provider: string;
  content: string;
  hotelChainId: string;
  chain: string;
  rooms: number;
  common_area: number;
  publicArea: number;
}

export interface ITransactionReport {
  hotelChainId: string;
  hotelId: string;
  roomId: string;
  chain: string;
  premise: string;
  room: string;
  date: string;
  type: string;
  asset: string;
  price: number | string;
}

// -- TEMPLATES --
export interface ITemplate {
  content: string;
  name: string;
  subject?: string;
}

// -- ORDERS --

export type PremiseOrderRequest = Omit<
  IPremiseOrder,
  | "orderNumber"
  | "creator"
  | "creatorCompany"
  | "dateCreated"
  | "dateSigned"
  | "dateCanceled"
  | "dateCompleted"
>;

export interface IPremiseOrder {
  orderNumber: string;
  orderStatus?: PremiseOrderStatus;
  premise: IPremise;
  order: IPremiseOrderLine[];
  pricing: IPremiseOrderPricing;
  invoice: IInvoice;
  creator?: string;
  creatorCompany?: Company;
  dateCreated?: string;
  dateSigned?: string;
  dateCanceled?: string;
  dateCompleted?: string;
}

export interface IPremise {
  name: string;
  company: string;
  organizationNumber: string;
  street: string;
  city: string;
  postalCode: string;
  contactName: string;
  contactEmail: string;
  signerName: string;
  signerEmail: string;
  primaryMarket?: Market;
}

export interface IPremiseOrderLine {
  roomType: RoomTypeCategory;
  numberOfRooms: number;
  content: UpsalesProduct[];
}

export interface IPremiseOrderPricing {
  pricingRows: IPricingRow[];
  //commission: number;
}

export interface IPricingRow {
  productId: number;
  productName: string;
  units: number;
  price: number;
  listPrice: number;
  commission: number;
  currency: string;
  type: string;
}

export interface IInvoice {
  street: string;
  city: string;
  postalCode: string;
  email: string;
  contractLength?: number;
  startDate?: Date;
  invoiceMarking?: string;
}

export enum EmailTemplateNames {
  INVOICE = "invoice",
  INVOICES = "invoices",
  DELETE_DATA = "deleteData",
  PREMISEORDER = "premiseOrder",
  PREMISEORDER_SIGNED = "premiseOrderSigned",
  USER_CREATED = "userCreated",
  USER_RESET_PASSWORD = "userResetPassword",
  ELGIGANTEN_ORDER_CONFIRMATION = "ElgigantenOrderConfirmation",
  ELGIGANTEN_ORDER_SIGNED = "ElgigantenOrderSigned",
  CONTENT_ORDER_USER_CONFIRMATION = "contentOrderConfirmation",
  CONTENT_ORDER = "contentOrder",
}

export enum Market {
  HOTEL = "hotel",
  CARE = "care",
  GYM = "gym",
  OFFICE = "office",
  RESTAURANT = "restaurant",
}

export enum PremiseOrderStatus {
  ORDERED = "ordered", // order has been recieved
  SIGNED = "signed", // order has been signed by the customer
  COMPLETED = "completed", //order has been part of a monthly report that has been sent to parter (e.g Elgiganten)
  CANCELED = "canceled", // order has been canceled (e.g. customer never signed)
}

// product ID, commission rate in percent
export type ProductCommissions = Record<number, number>;
// product ID, max discount in percent
export type ProductDiscount = Record<number, number>;

export type UpsalesProduct = any; //TODO: need the correct type
export interface UpsalesProducts {
  content: {
    ca: Array<UpsalesProduct>;
    public: Array<UpsalesProduct>;
    room: Array<UpsalesProduct>;
  };
  generic: Array<UpsalesProduct>;
}

// -- Content order --
export interface ContentOrder {
  orderRows: ContentOrderRow[];
}

export interface ContentOrderRow {
  roomId: string;
  content: UpsalesProductId[];
}

export interface OrderMailData {
  premiseId: string;
  premiseName: string;
  ordererMail: string;
  orderRows: DetailedOrderRow[];
  totalPrice: string;
  upsalesCompanyId: number;
}

export interface OrderRowContent {
  name: string;
  price: string;
  upsalesProductId: number;
  offerings: string[];
  roomTypeCategory: RoomTypeCategory;
}

export interface DetailedOrderRow {
  roomId: string;
  roomName: string;
  currentContent: OrderRowContent[];
  content: OrderRowContent[];
  groupedContent: {
    packageNames: string;
    detailedPackageNames: string;
    combinedPackagePrice: string;
  };
}

export interface ContentOrderResponse {
  wasOrderMailSent: boolean;
  wasUserConfirmationMailSent: boolean;
  orderSentMessageInfo: SentMessageInfo;
  userSentMessageInfo: SentMessageInfo;
  error: any;
  orderDetails: OrderMailData;
}

// --- COMPANY ---

export interface Company extends IBaseModel {
  name: string;
  parent?: Company | string;
  type: CompanyType;
  organizationNumber?: string;
  externalId?: string;
  upsalesId?: string;
  signageConfig?: ISignageConfig;
  canManagePremises: boolean;
}

export enum CompanyType {
  RESELLER = "reseller",
  SYSTEM_INTEGRATOR = "systemintegrator",
  CUSTOMER = "customer",
  ADVERTISER = "advertiser",
}

// --- SIGNAGE ---

export enum OverlayPosition {
  TOP_LEFT = "top-left",
  TOP_RIGHT = "top-right",
  BOTTOM_LEFT = "bottom-left",
  BOTTOM_RIGHT = "bottom-right",
}

export interface ImageOverlay {
  locator: string;
  thumbnailLocator?: string;
  position: OverlayPosition;
}

export enum AssetSource {
  NENDA = "Nenda",
  REDBEE = "RedBee",
  EXTERNAL = "External",
}

export enum AssetType {
  IMAGE = "image",
  VIDEO = "video",
  LINEAR_CHANNEL = "linearchannel",
  URL = "url",
  HTML = "html",
}

/**
 * Whether a URL asset can be rendered inside an iframe on a screen.
 *
 * Stored on the asset so the warning is permanent rather than a one-off alert
 * shown while creating it. `UNVERIFIED` exists so that an environment without a
 * headless browser degrades to "we don't know" instead of a false negative.
 */
export enum UrlEmbedStatus {
  EMBEDDABLE = "embeddable",
  BLOCKED = "blocked",
  UNVERIFIED = "unverified",
}

export enum UrlEmbedBlocker {
  X_FRAME_OPTIONS = "x-frame-options",
  CONTENT_SECURITY_POLICY = "content-security-policy",
  HTTP_STATUS = "http-status",
  UNREACHABLE = "unreachable",
}

export interface IUrlEmbedCheck {
  status: UrlEmbedStatus;
  checkedAt: Date;
  blockedBy?: UrlEmbedBlocker;
  /**
   * Diagnostic text for support and logs, e.g. `X-Frame-Options: DENY`.
   *
   * NEVER render this in the portal: it is server-generated English and would
   * both leak implementation detail to hotel users and bypass i18n. User-facing
   * copy is derived from `blockedBy` instead (see UrlEmbedBadge).
   */
  detail?: string;
}

export enum BookingPriority {
  HIGH = 1,
  MEDIUM = 2,
  LOW = 3,
}

export enum ScheduleSlotType {
  SIGNAGE = "Signage",
  ADVERT = "Ad",
  CONTENT = "Content",
}

export enum ContentScheduleType {
  SCHEDULED = "scheduled",
  OVERRIDE = "override",
  DEFAULT = "default",
}

export type ScheduleSlots = Partial<Record<Minute, ILeanAsset[]>>;

export enum ScheduledCommand {
  REBOOT = "reboot",
  LOGOUT = "logout",
}

export interface IAsset extends IBaseModel {
  name: string;
  premise?: Document["_id"];
  company: Document["_id"];
  product: NendaProduct;
  locator: string;
  thumbnailLocator?: string;
  type: AssetType;
  source: AssetSource;
  fileSize?: number;
  mimeType?: string;
  dimensions?: {
    width: number;
    height: number;
  };
  /** Only set for `AssetType.URL` assets. */
  embedCheck?: IUrlEmbedCheck;
}

export type ILeanAsset = Pick<
  IAsset,
  | "name"
  | "locator"
  | "type"
  | "source"
  | "_id"
  | "mimeType"
  | "fileSize"
  | "dimensions"
>;

export type FileUploadScope = FileUploadType.LOGO | FileUploadType.POSTER;
export enum FileUploadType {
  LOGO = "logo",
  POSTER = "poster",
}

export interface LogoOverlay extends ImageOverlay {
  useInPlayer: boolean;
}

export interface WebTokenSessionData {
  role: Role | undefined;
  expires: number | undefined;
  username: string | undefined;
  company: string | undefined;
  premises: string[] | undefined;
  issuedAt: number | undefined;
}

export interface IAdPermission {
  external: ITakeoverPermission;
  signage: ITakeoverPermission;
}

export interface ITakeoverPermission {
  fullTakeoverAllowed: boolean;
  bannerAllowed: boolean;
}

export interface IContentChannel extends IBaseModel {
  name: string;
  description: string;
  category: string;
  playlist: ILeanAsset[];
  hasSound: boolean;
  isPublic: boolean;
  isAutoShuffleEnabled: boolean;
  adPermissions: IAdPermission;
  sortOrder: number;
  logo?: LogoOverlay;
  posterUrl?: string;
  previewImageUrl?: string;
  previewVideoUrl?: string;
  branding?: {
    allowed: boolean;
    brandingPositionOverride?: OverlayPosition;
  };
}

export interface ISignagePlaylistItem {
  asset: Document["_id"];
  durationInSeconds?: number;
}

export interface ISignagePlaylist extends IBaseModel {
  name: string;
  company: Document["_id"];
  premise?: Document["_id"];
  items: ISignagePlaylistItem[];
  defaultItemDuration: number;
  isSoundEnabled: boolean;
}

export interface Time {
  hour: Hour;
  minute: Minute;
}

export interface IPromotionSchedule {
  date: DateRange;
  time: {
    start: Time;
    end: Time;
  };
  days: DayOfWeek[];
}

export interface ISignagePromotion extends IBaseModel {
  name: string;
  company: Document["_id"];
  premise?: Document["_id"];
  playlist: IPromotionPlaylist;
  schedule: IPromotionSchedule;
  isHighPriority: boolean;
}

export type IPromotionPlaylist = IPromotionPlaylistItem[];

export interface IPromotionPlaylistItem {
  asset: Document["_id"];
  durationInSeconds: number;
  sortOrder: number;
}

export type PopulatedPromotionPlaylistItem = Omit<
  IPromotionPlaylistItem,
  "asset"
> & {
  asset: IAsset;
};

export type SignagePromotionWithPopulatedPlaylist = Omit<
  ISignagePromotion,
  "playlist"
> & {
  playlist: PopulatedPromotionPlaylistItem[];
};

export interface IDailyContentPlaylist extends IBaseModel {
  name: string;
  premise?: Document["_id"];
  company: Document["_id"];
  timeline: ContentTimeline;
}

export type ContentTimeline = ITimeLineItem[];

export interface ITimeLineItem {
  content: Document["_id"];
  contentRefType: ScheduledContentRefType;
  interval: ITimeInterval;
}

export interface ITimeInterval {
  start: Time;
  end: Time;
}

export enum Scope {
  CanCreate = "can-create",
  CanEdit = "can-edit",
  CanOrder = "can-order",
  CanDelete = "can-delete",
  CanView = "can-view",
  CanAdministrate = "can-administrate",
}

export enum Resource {
  Premise = "premise",
  User = "user",
  Company = "company",
  Unit = "unit",
  Ads = "ads",
  ProductOfferings = "productOfferings",
  ContentChannel = "contentChannel",
  Asset = "asset",
  FrontlayerConfig = "frontlayerConfig",
  SignagePromotion = "signagePromotion",
  SignagePlaylist = "signagePlaylist",
  DailyContentPlaylist = "dailyContentPlaylist",
  Content = "content", // Temporary resource for general content management
  Group = "group",
}

export type OrganizationType = Resource.Company | Resource.Premise;

export type Permission = Partial<Record<Role, Scope[]>>;
export type ResourcePermissions = Record<Resource, Permission>;
export type Restriction = { resource: Resource; scopes: Scope[] };

export type PatchRequest<T> = {
  data: Partial<T>;
  unsetFields?: NestedKeys<T>[];
};

export type ContentChannelPatchRequest = PatchRequest<IContentChannel> & {
  /** The `updatedAt` the editor loaded; the save is refused with 409 if the channel changed since. */
  expectedUpdatedAt?: string;
};

export enum CacheLevel {
  All = 0,
  PremiseGroup = 1,
  Premise = 2,
  Room = 3,
}

export enum GroupableObject {
  Screen = "screen",
}

export enum OfferingType {
  ADMINISTRATIVE = "administrative",
  RENTAL = "rental",
  //SUBSCRIPTION = "subscription",
  //PURCHASE = "purchase",
  //EVENT_TICKET = "event_ticket",
}

// Offerings
export interface IFeatureSet extends IBaseModel {
  name: string;
  description: string;
  imageUrl?: string;
  businessUnit: string;
  localizedMetadata?: Array<{
    locale: string;
    name: string;
    description?: string;
  }>;
  externalRef: string;
  type: OfferingType;
  enabled: boolean;
  mediaCollections: string[]; // ID references
  price?: {
    currency: string;
    amount: number;
    fractionDigits: number;
  };
  products: {
    product: NendaProduct;
    features: string[];
  }[];
}

// TODO: clean this
export enum FEATURE_SET_FEATURES {
  "CONTINOUS_STREAMING" = "Continuous Streaming",
  "OWN_VIDEOS" = "Own Videos",
  "FAST_CHANNELS" = "Fast Channels",
  "SIGNAGE" = "Signage",
  "EXTERNAL_ADS" = "External Ads",
}

// CMS
export interface IMediaCollection extends IBaseModel {
  name: string;
  description: string;
  enabled: boolean;
  //type: string;
  //collectionType: string;
  //query: string[];
  assets: string[];
  channels?: string[];
}

export interface ISession extends IBaseModel {
  company?: string;
  premiseId: string;
  unitId: string;

  //sessionToken: string;
  technicalProduct: string;
  expiryDate: Date | string;
  guestSession?: string;
  settings: {
    appLanguage: string;
    subsLanguage: string;
    subsEnabled: boolean;
    audioLanguage: string;
  };
  ttl?: number;
}

export interface IContinueWatching {
  assetId: string;
  position: number;
  completed: boolean;
}

export interface IGuestInformation {
  firstName?: string;
  lastName?: string;
  title?: string;
  defaultLanguage?: string;
  email?: string;
}

export interface IGuestPurchase {
  purchase: IPurchase["_id"];
  entitlement: IFeatureSet["_id"];
  expiresAt: Date;
}

export interface IGuestSessionData extends IBaseModel {
  guestInformation?: IGuestInformation;
  expiresAt: Date;
  ttl: number;
  purchases: IGuestPurchase[];
  continueWatching: IContinueWatching[];
}

export interface IGuestSession extends IBaseModel {
  unitId: string;
  expiresAt: Date;
  ttl: number;
  sessionData?: IGuestSessionData["_id"];
}

// Note that while the token is specific for a device,
// the session is for a unit
export interface DeviceSessionToken {
  deviceId: string;
  premiseId: string;
  unitId: string;
  sessionId: string;
}

// End CMS
export interface IGroup extends IBaseModel {
  name: string;
  applicableTo: GroupableObject[];
  company: Document["_id"];
  premise?: Document["_id"];
}

// -- Ingest Jobs --
/**
 * The phases an ingest job moves through, in order. Mirrors `INGEST_JOB_PHASES` in
 * `content-transfer-orchestrator`, which owns every phase transition.
 *
 * The names are cut by who is responsible for the work:
 * - `transcoding` — the transcoder is working. Ends when it reports a terminal status.
 * - `assembly` — ours. The package is produced, uploaded to Ålcom's FTPS and registered with them.
 * - `ingesting` — Ålcom's. They take the registered package and make it playable.
 */
export type IngestJobPhase =
  | "queued"
  | "transcoding"
  | "assembly"
  | "ingesting"
  | "publishing"
  | "completed"
  | "failed"
  | "cancelled";

export interface IIngestPhaseEntry {
  phase: IngestJobPhase;
  startedAt: Date;
  completedAt?: Date;
  progress?: number;
  error?: string;
  retryCount?: number;
  lastRetryAt?: Date;
  /** Phase-specific detailed status. */
  details?: {
    encoreStatus?: string;
    encoreMessage?: string;
    encoreStartedDate?: string;
    encoreCompletedDate?: string;
    encoreJobResponse?: any; // Full Encore job response (initial + completed)
    /**
     * Open Videocore's own reported status for the transcode job (`pending`, `queued`, `running`,
     * `done`, `failed`, `cancelled`). Separate from `encoreStatus` so neither key ever holds the
     * other vocabulary's words.
     */
    videocoreStatus?: string;
    /**
     * Videocore's `job.error`. A HINT, never a diagnosis: a reconciler upstream overwrites the
     * real cause, and the first job run this way reported an Encore problem for what was an
     * unresolvable profile. `videocoreEncoreInstanceId` is what to go and read instead.
     */
    videocoreMessage?: string;
    videocoreJobResponse?: any; // Full Videocore job response
    /**
     * Status and percentage of the leg that pulls the source into the stack's own bucket, which
     * has to finish before a transcode can be requested. Kept apart from `videocoreStatus` so
     * neither key ever holds the other leg's answer, and deliberately not published as
     * `progress`: running the portal's bar to 100 and then back to 0 when the encode starts
     * would report the transfer as the transcode.
     */
    videocoreSourceStatus?: string;
    videocoreSourceProgress?: number;
    /**
     * True when the most recent attempt to read live progress off the spawned Encore instance
     * FAILED, as opposed to finding the instance legitimately gone. The two look identical
     * downstream — both leave the percentage where it was — but only one of them is evidence
     * about the encode.
     */
    videocoreProgressUnreadable?: boolean;
    /**
     * Consecutive failures to start a transcode for a source Videocore has already pulled. Reset
     * the moment one is accepted; drives the alarm that stops a permanently refused request — an
     * unresolvable profile, typically — from sitting at 0% with an empty `incidents`.
     */
    videocoreTranscodeRequestFailures?: number;
    /**
     * Set when Videocore reports the encode was interrupted — by a scale-down, typically. It is
     * recoverable and auto-retried, and the status stays `running`, so this is recorded for the
     * reader rather than treated as a failure.
     */
    videocoreInterruptionReason?: string;
    /**
     * OSC instance name of the ephemeral Encore the Videocore scaler spawned. The place to read
     * the encode's real log, and the source of live progress — but it is torn down a few minutes
     * after going idle, so it is often already gone by the time anyone looks.
     */
    videocoreEncoreInstanceId?: string;
    /**
     * ISO timestamp of the last time the transcoder's reported state (status or progress) actually
     * CHANGED — not the last poll. The stall detector measures movement against this. The name
     * predates the second transcoder; it is written on both paths.
     */
    lastEncoreChangeAt?: string;
    /**
     * ISO timestamp of when Encore accepted a cancel for this job. Its worker slot is free from
     * that moment. Encore only: Videocore reports a successful cancel without stopping the encode,
     * so nothing on that path writes this.
     */
    transcodeCancelledAt?: string;
    /**
     * Consecutive failed transcoder polls for this job. Reset on any successful poll; drives the
     * `encore_unreachable` alarm. The name predates the second transcoder; it is written on both
     * paths.
     */
    encorePollFailures?: number;
    /**
     * ISO timestamp of when the poller first saw the transcoder report the transcode finished. The
     * package wait measures from here, not from when the phase started. The name predates the
     * second transcoder; it is written on both paths.
     */
    encoreFinishedAt?: string;
    /** ISO timestamp of the last MinIO/backup listing run while waiting for the package. */
    lastPackageProbeAt?: string;
    /** ISO timestamp of the last time the assembly step wrote a rendition or the playlist. */
    packageAssemblyProgressAt?: string;
    /** ISO timestamp a complete package was first seen in MinIO during this stay in `assembly`. */
    packageCompleteAt?: string;
    alcomState?: string;
    alcomAdditionalInfo?: string;
    alcomPlayable?: boolean;
    alcomContentDurationMs?: string;
    alcomCreationTime?: string;
    alcomModificationTime?: string;
    alcomDeliveryUris?: {
      dash?: string;
      hls?: string;
      mss?: string;
    };
    notFoundBudgetSeconds?: number;
    stuckBudgetSeconds?: number;
    /**
     * The storage probe's operator-facing sentence, kept so the ingesting timeout can reuse it on
     * a later tick. The incident carries the evidence; this carries what the portal shows.
     */
    storageProbePortalMessage?: string;
    /** The assembly step's operator-facing sentence, written for the portal to show. */
    packageAssemblyPortalMessage?: string;
  };
}

/**
 * The incident kinds the orchestrator records on a job. `incidents[].type` is an unconstrained
 * `String` in both repos' schemas, so a value missing here still persists and still renders — this
 * union is about narrowing being correct in this repo, not about unblocking a write.
 *
 * Derived from the incident-type constants in `content-transfer-orchestrator` on
 * `feature/videocore-transcoder`; `src/shared/transcodeStall.ts`, `src/shared/packageAssembly.ts`,
 * `src/shared/packageHandoff.ts`, `src/shared/dispatchLimit.ts`, `src/shared/transcoder.ts`,
 * `src/shared/ingestDiagnosis.ts` and the `const *_INCIDENT_TYPE` declarations in
 * `src/functions/` are where they are declared.
 */
export type IngestIncidentType =
  | "alcom_502_repost"
  | "phase_timeout"
  | "metadata_warning"
  | "image_warning"
  | "ingesting_retry"
  | "storage_probe"
  | "assembly_refusal_leaked"
  | "assembly_unavailable_here"
  | "assembly_unverifiable_here"
  | "dispatch_refused"
  | "encore_status_unknown"
  | "encore_unreachable"
  | "job_retry"
  | "package_assembly_failed"
  | "package_assembly_unavailable"
  | "package_probe_unavailable"
  | "renditions_missing"
  | "transcode_cancelled"
  | "transcode_request_failed"
  | "transcode_failed"
  | "transcode_stalled"
  | "transcode_status_unknown"
  | "transcoder_unreadable"
  | "transfer_unconfirmed";

/**
 * Which transcoder produced a job's renditions. Frozen on the job at dispatch rather than
 * derived from the transcoder setting on read: the setting changes over time, and a job's
 * renditions only exist in the storage of the transcoder that produced them, so a retry has
 * to go back to the same one. Absent means "encore" — jobs predating the field carry no value.
 */
export type IngestTranscoder = "encore" | "videocore";

export interface IIngestIncident {
  type: IngestIncidentType;
  phase?: IngestJobPhase;
  occurredAt: Date;
  resolvedAt?: Date;
  attempt: number;
  details?: string;
  itemPath?: string;
  metadataPath?: string;
}

export interface IIngestJob extends IBaseModel {
  encoreJobId?: string;
  /** Full URL to poll Encore job status (e.g. .../encoreJobs/{id}). Set by ingestTrigger. */
  encoreJobStatusUrl?: string;
  createdBy: string; // email address of the internal user who triggered the ingest
  sourceUrl: string;
  assetId: string;
  /** If part of a bulk ingest, references the parent bulkIngestJob._id. Null for manual ingests. */
  bulkJobId?: string;
  /** Dispatch priority. 10 = manual (high), 1 = bulk (low). */
  priority: number;
  /** Encore pipeline name (e.g. 'nenda'). */
  pipelineName: string;
  /** Options passed to Encore VOD trigger (profile, baseName, externalId). */
  vodOptions?: Record<string, unknown>;
  /** MediaCollection IDs to add the asset to after publishing completes. */
  entitlements?: string[];
  /** ContentChannel IDs whose playlist gets the asset appended, after the entitlements are applied. */
  contentChannels?: string[];
  /** Permanent record of notable incidents during the job lifecycle (e.g. Alcom 502 retries). */
  incidents?: IIngestIncident[];
  /**
   * Which transcoder the orchestrator dispatched this job to. Keep it top level: the
   * orchestrator replaces the whole `currentPhase` subdocument on each phase transition, so
   * a field nested there and written by another process is dropped on the next transition.
   */
  transcoder?: IngestTranscoder;
  /**
   * Where this job lives inside Open Videocore, written once at dispatch by the orchestrator.
   *
   * Top level for the same reason as `transcoder` and `transfer`: the orchestrator replaces the
   * whole `currentPhase` subdocument on each phase transition, so anything nested there is gone
   * by the time the package has to be found.
   *
   * `videocore.assetId` is a ULID Videocore mints for its own asset record. It is not the CMS
   * asset id, which is the top-level `assetId`.
   *
   * Absent on Encore jobs and on every job predating the Videocore path.
   */
  videocore?: {
    /** Instance base URL the job was dispatched to, as configured at that moment. */
    baseUrl: string;
    /** The stack that owns the source bucket, the packaged bucket and the queue. */
    stack: string;
    /** Videocore's own asset id — see the note above on why it is not `assetId`. */
    assetId: string;
    /** The job that pulls the source into the stack's source bucket. */
    sourceJobId: string;
    /** The transcode job. Absent while the source pull is still running. */
    transcodeJobId?: string;
    /** The profile requested, resolved by name from the instance's profile store. */
    profile: string;
  };
  /**
   * What the orchestrator did with this asset's package: which MinIO directory it came from and
   * where its files were put. Top level for the same reason as `videocore` above.
   *
   * Best-effort by construction on the orchestrator's side, so an absent `transfer` means "not
   * recorded", never "not transferred".
   */
  transfer?: {
    /** The MinIO directory the packager chose: the second segment of `<assetId>/<packageId>/`. */
    packageId?: string;
    /** The absolute FTPS directory actually used. */
    ftpsRemoteDir?: string;
    ftpsUploadedAt?: Date;
    alcomPostedAt?: Date;
    /** The `upload.location` sent to Ålcom. */
    alcomUploadLocation?: string;
  };
  /** Current phase - updated by poller. Indexed on phase field. */
  currentPhase: IIngestPhaseEntry;
  /** Completed phases (history). */
  phaseLog: IIngestPhaseEntry[];
  manifestUrls?: {
    dash: string;
    hls: string;
  };
  startedAt?: Date;
  completedAt?: Date;
}

// -- Bulk Ingest Jobs --
export type BulkIngestJobStatus =
  | "created"
  | "validated"
  | "preparing"
  | "preparing_failed"
  | "processing"
  | "completed"
  | "failed"
  /** Cancellation requested by the portal; the orchestrator has not drained the queue yet. */
  | "cancelling"
  | "cancelled";

export type BulkIngestItemStatus =
  | "pending"
  | "prepared"
  | "completed"
  | "failed"
  | "cancelled";

export interface IBulkIngestItem {
  videoPath: string;
  metadataPath?: string;
  subtitlePaths?: string[];
  imagePaths?: string[];
  status: BulkIngestItemStatus;
  /** Set once prepared — links to ingestJobs collection via MongoDB ObjectId reference */
  ingestJobId?: string;
  /** Set once CMS asset is created */
  assetId?: string;
  error?: string;
}

/** API response type: IBulkIngestItem with the referenced IIngestJob joined on read. */
export interface IBulkIngestItemResponse extends IBulkIngestItem {
  ingestJob?: IIngestJob;
}

/** API response type: IBulkIngestJob with items typed as IBulkIngestItemResponse. */
export interface IBulkIngestJobResponse extends Omit<IBulkIngestJob, "items"> {
  items: IBulkIngestItemResponse[];
}

export interface IBulkIngestJobGlobalMetadata {
  provider?: string;
  tags?: string[];
  entitlements?: string[];
  contentChannels?: string[];
}

export interface IBulkIngestJob extends IBaseModel {
  name: string;
  description?: string;
  createdBy: string;
  status: BulkIngestJobStatus;
  /** Blob container path where the uploaded package is stored */
  storagePath: string;
  items: IBulkIngestItem[];
  totalItems: number;
  preparedItems: number;
  completedItems: number;
  failedItems: number;
  cancelledItems: number;
  globalMetadata?: IBulkIngestJobGlobalMetadata;
  incidents?: IIngestIncident[];
  startedAt?: Date;
  completedAt?: Date;
  error?: string;
}

declare module "fastify" {
  export interface InternalApiRequest extends FastifyRequest {
    decodedSession: UserSession;
    params: any;
  }

  export interface ExternalSessionApiRequest extends FastifyRequest {
    deviceSessionToken: DeviceSessionToken;
    session: ISession;
    params: any;
  }
}

export enum NotificationType {
  ERROR = "error",
  WARNING = "warning",
  INFO = "info",
  SUCCESS = "success",
  DEFAULT = "default",
}

export type NotificationSeverity = NotificationType;
